/**
 * Human-readable descriptions for document types in the Create Document dropdown.
 * Matches canonical SOW types; falls back for admin-created custom names.
 */
const DESCRIPTION_RULES = [
	{
		pattern: /academic\s*research\s*[-–—]\s*student|\bstudent\b.*research|academic\s*paper|academic\s*latest/i,
		description: 'Course papers and short assignments (APA student paper structure).'
	},
	{
		pattern:
			/academic\s*research\s*[-–—]\s*professional|\bprofessional\s*manuscript|\bprofessional\b.*research/i,
		description: 'Journal articles and conference papers (professional manuscript formatting).'
	},
	{
		pattern: /honors\s*thesis/i,
		description: 'Undergraduate capstone and honors thesis projects.'
	},
	{
		pattern: /master'?s\s*thesis|\bmaster\s*thesis|thesis\s*latest/i,
		description: "Graduate master's thesis with advisor and committee sections."
	},
	{
		pattern: /phd|doctoral|dissertation/i,
		description: 'PhD dissertation with front matter, chapters, and references.'
	}
];

const DEFAULT_DESCRIPTION =
	'Structured academic document with APA formatting and section templates built in.';

/** Canonical SOW display names for legacy admin/DB labels like "Dissertation Latest 2". */
const DISPLAY_NAME_RULES = [
	{
		pattern: /academic\s*research\s*[-–—]\s*professional|professional\s*manuscript/i,
		label: 'Academic research – Professional Manuscript'
	},
	{
		pattern: /academic\s*research\s*[-–—]\s*student|academic\s*latest|academic\s*paper/i,
		label: 'Academic research – Student'
	},
	{ pattern: /honors\s*thesis/i, label: 'Honors Thesis' },
	{ pattern: /master'?s\s*thesis|\bmasters?\s*thesis|thesis\s*latest/i, label: "Master's Thesis" },
	{ pattern: /phd|doctoral|dissertation(\s*latest)?/i, label: 'PhD Dissertation' }
];

/**
 * Map stored DocumentType.name (including "Dissertation Latest 2") to SOW label for UI.
 * @param {string | { name?: string } | null | undefined} documentTypeOrName
 * @returns {string}
 */
export function getDocumentTypeDisplayName(documentTypeOrName) {
	const raw =
		documentTypeOrName && typeof documentTypeOrName === 'object'
			? String(documentTypeOrName.name || '').trim()
			: String(documentTypeOrName || '').trim();
	if (!raw) return '';

	// Already a clean SOW name — keep as-is (normalize whitespace only)
	if (
		/^academic research\s*[–—-]\s*(student|professional)/i.test(raw) ||
		/^honors thesis$/i.test(raw) ||
		/^master'?s thesis$/i.test(raw) ||
		/^phd dissertation$/i.test(raw)
	) {
		return raw.replace(/\s+/g, ' ').trim();
	}

	for (const { pattern, label } of DISPLAY_NAME_RULES) {
		if (pattern.test(raw)) return label;
	}

	// Strip version suffixes: "Something Latest 2", "Something -2", "Something Latest-2"
	const cleaned = raw
		.replace(/\s*[-–—]?\s*\d+\s*$/i, '')
		.replace(/\s+latest\b/i, '')
		.replace(/\s+/g, ' ')
		.trim();
	return cleaned || raw;
}

/**
 * @param {string | { name?: string; description?: string } | null | undefined} documentTypeOrName
 * @returns {string}
 */
export function getDocumentTypeDescription(documentTypeOrName) {
	if (documentTypeOrName && typeof documentTypeOrName === 'object') {
		if (documentTypeOrName.description && String(documentTypeOrName.description).trim()) {
			return String(documentTypeOrName.description).trim();
		}
		if (documentTypeOrName.name) {
			return getDocumentTypeDescription(documentTypeOrName.name);
		}
		return DEFAULT_DESCRIPTION;
	}

	const name = typeof documentTypeOrName === 'string' ? documentTypeOrName.trim() : '';
	if (!name) return DEFAULT_DESCRIPTION;

	const display = getDocumentTypeDisplayName(name);
	for (const { pattern, description } of DESCRIPTION_RULES) {
		if (pattern.test(display) || pattern.test(name)) return description;
	}
	return DEFAULT_DESCRIPTION;
}
